import { create } from 'zustand';

export type UploadStatus = 'idle' | 'uploading' | 'success' | 'error' | 'cancelled';

export interface UploadTask {
  id: string;
  fileName: string;
  progress: number;
  status: UploadStatus;
  statusText?: string;
  error?: string;
  meta?: Record<string, any>;
  abortController?: AbortController;
  // Chunked upload specific fields
  isChunked?: boolean;
  sessionID?: string;
  file?: File;
  chunkSize?: number;
  // Resume handler - stored by the service to avoid circular dependency
  onResume?: () => Promise<void>;
}

interface UploadStore {
  uploads: UploadTask[];
  addUpload: (upload: Omit<UploadTask, 'progress' | 'status'>) => void;
  updateUpload: (id: string, updates: Partial<UploadTask>) => void;
  removeUpload: (id: string) => void;
  cancelUpload: (id: string) => void;
  clearFinished: () => void;
  getUploadById: (id: string) => UploadTask | undefined;
}

export const useUploadStore = create<UploadStore>((set, get) => ({
  uploads: [],
  
  addUpload: (upload) =>
    set((state) => ({
      uploads: [
        ...state.uploads,
        {
          ...upload,
          progress: 0,
          status: 'uploading' as UploadStatus,
        },
      ],
    })),
  
  updateUpload: (id, updates) =>
    set((state) => ({
      uploads: state.uploads.map((upload) =>
        upload.id === id ? { ...upload, ...updates } : upload
      ),
    })),
  
  removeUpload: (id) =>
    set((state) => ({
      uploads: state.uploads.filter((upload) => upload.id !== id),
    })),
  
  cancelUpload: (id) =>
    set((state) => {
      const upload = state.uploads.find((u) => u.id === id);
      if (upload?.abortController) {
        console.log('Aborting upload:', id);
        upload.abortController.abort();
      }
      return {
        uploads: state.uploads.map((u) =>
          u.id === id
            ? { ...u, status: 'cancelled' as UploadStatus, abortController: undefined }
            : u
        ),
      };
    }),
  
  clearFinished: () =>
    set((state) => ({
      uploads: state.uploads.filter(
        (upload) => upload.status === 'uploading' || upload.status === 'idle'
      ),
    })),

  getUploadById: (id) => {
    return get().uploads.find((u) => u.id === id);
  },
}));


